import type { TypedModelId } from '@picsart/ai-sdk';

import { client } from 'services/sdkClient';

import { resolveModelId } from './constants';
import { getSupportedAspectRatios, pickAspectRatio } from './aspectRatio';

export interface IAiEditImageRequest {
  sourceUrl: string;
  prompt: string;
  modelId: string;
  aspectRatio: string | null;
  referenceUrl: string | null;
  // Source dimensions used to derive a ratio when aspectRatio is 'auto' (null).
  sourceWidth?: number;
  sourceHeight?: number;
}

/**
 * Single source of truth for an AI Editor generation call — used by both the live generate flow
 * (useAIEditorGenerate) and preset replay (aiEditProcess). Returns the generated image URL.
 */
export const generateAiEditImage = async ({ sourceUrl, prompt, modelId, aspectRatio, referenceUrl, sourceWidth, sourceHeight }: IAiEditImageRequest): Promise<string> => {
  const ratio = aspectRatio ?? (sourceWidth && sourceHeight ? pickAspectRatio(sourceWidth, sourceHeight, getSupportedAspectRatios(modelId)) : null);

  const params: Record<string, unknown> = { imageUrls: referenceUrl ? [sourceUrl, referenceUrl] : [sourceUrl], prompt };
  if (ratio) params.aspectRatio = ratio;

  const result = await client.generate(resolveModelId(modelId) as TypedModelId, params as never);
  const url = result.results?.[0]?.url || result.url;
  if (!url) {
    throw new Error('AI Editor generation returned no image');
  }

  return url;
};
